/**
 * The employer marketing surface.
 *
 * The tagline is the one line of voice on the page. Everything under it is a
 * rule, a figure, or a term, written the way the payment code behaves.
 */

import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

import {
  LISTING_LIFETIME_DAYS,
  MINIMUM_FIRST_BID_CENTS,
  MINIMUM_RAISE_INCREMENT_CENTS,
  PASS_INCREMENT_CENTS,
} from '@bidtohire/shared';

import { PUBLISH_FROM_LABEL } from '../lib/bid-pricing';
import { Money } from '../components/Money';
import { ArrowRight, MoneyWhereMouthIs } from '../components/ui';
import { PricingTrio } from './pricing-shared';

const VALUE_PROPS: readonly {
  readonly n: string;
  readonly title: string;
  readonly body: ReactNode;
}[] = [
  {
    n: '01',
    title: 'A narrow board is never dearer to lead',
    body: (
      <>
        Every category and specialism has its own board, and your listing sits on all three at
        once. A specialism leader is never ahead of its category leader, so leading the narrow
        board never costs more — and an empty specialism is led by the first bid alone.
      </>
    ),
  },
  {
    n: '02',
    title: 'The board is live',
    body: (
      <>
        Every board reorders the moment a payment completes, and each row states the exact total
        needed to claim its position. Raise from your dashboard whenever you want one back.
      </>
    ),
  },
  {
    n: '03',
    title: 'Views and outbound clicks',
    body: (
      <>
        Every listing reports how many times it was viewed and how many people clicked through to
        your posting.
      </>
    ),
  },
];

export function ForEmployersPage(): ReactNode {
  return (
    <>
      <section className="mkt-hero">
        <div className="gradwash" />
        <div className="inner">
          <span className="eyebrow">For employers</span>
          <h1 style={{ marginTop: 12 }}>
            <MoneyWhereMouthIs />
          </h1>
          <p className="lede">
            <Money cents={MINIMUM_FIRST_BID_CENTS} /> posts a listing. Rank is the total paid —
            nothing else. Candidates see that figure on every row.
          </p>
          <div className="actions">
            <Link className="pill pill-primary" to="/post">
              Post a job — {PUBLISH_FROM_LABEL}
              <ArrowRight />
            </Link>
            <Link className="pill pill-ghost" to="/">
              See what #1 costs today
            </Link>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="inner">
          <div className="section-heading">
            <div>
              <h2>How ranking works</h2>
              <p>
                A listing ranks on the total its company has paid. No relevance score, no promoted
                tier.
              </p>
            </div>
          </div>

          <div className="valuegrid">
            {VALUE_PROPS.map((prop) => (
              <div className="card valuecard" key={prop.n}>
                <span className="icon">{prop.n}</span>
                <div className="title">{prop.title}</div>
                <p>{prop.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="inner">
          <div className="section-heading">
            <div>
              <h2>What it costs</h2>
              <p>One-time payments in whole dollars, through Stripe. No subscription, no contract.</p>
            </div>
          </div>
          <PricingTrio />
        </div>
      </section>

      <section className="section">
        <div className="inner">
          <div className="card is-padded">
            <span className="eyebrow">The terms, in full</span>
            <ul className="termslist">
              <li>
                <strong>
                  Posting a listing costs at least <Money cents={MINIMUM_FIRST_BID_CENTS} />, and
                  it is paid first.
                </strong>{' '}
                That payment also buys the listing’s opening position, so a larger first bid opens
                higher — at <Money cents={MINIMUM_FIRST_BID_CENTS} /> you start below every listing
                already holding that total. You then paste your posting’s address, we fill the form
                in from it, and the listing publishes the moment you submit it.
              </li>
              <li>
                <strong>
                  Every later raise adds at least <Money cents={MINIMUM_RAISE_INCREMENT_CENTS} />.
                </strong>{' '}
                Bids are placed in whole dollars.
              </li>
              <li>
                <strong>A listing’s position is its lifetime total paid.</strong> Raising is a
                top-up: you pay the difference between the total you hold and the total you want.
                A competitor has to beat the total, not the difference.
              </li>
              <li>
                <strong>
                  Bid any amount from <Money cents={MINIMUM_FIRST_BID_CENTS} />, and your listing
                  lands wherever that total puts it.
                </strong>{' '}
                Rank is the total paid, so passing a listing takes its total plus{' '}
                <Money cents={PASS_INCREMENT_CENTS} />. Ties keep the earlier bid ahead, so a row
                must be exceeded rather than matched, and the figure for the position your bid would
                actually land in is shown before you pay.
              </li>
              <li>
                <strong>Bids are cumulative and non-refundable.</strong> A position is claimed when
                the payment completes, not before.
              </li>
              <li>
                <strong>Listings run {LISTING_LIFETIME_DAYS} days and renew free.</strong> The total
                paid and the position carry over.
              </li>
              <li>
                <strong>One payment counts on all three boards.</strong> Global, your category,
                and your specialism.
              </li>
              <li>
                <strong>Equal totals stay in the order they were reached.</strong> The listing
                that got there first keeps the higher position.
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="section is-last">
        <div className="inner">
          <div className="card swagger">
            <div className="line">
              <Money cents={MINIMUM_FIRST_BID_CENTS} /> puts you on the board.
            </div>
            <p>Pay first, then paste your posting. We fill in the details from it.</p>
            <div className="row" style={{ marginTop: 20 }}>
              <Link className="pill pill-primary" to="/post">
                Post a job — {PUBLISH_FROM_LABEL}
                <ArrowRight />
              </Link>
              <Link className="pill pill-quiet" to="/about">
                How ranking works
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
